/**
 * Hub API client — thin fetch wrappers for the 5 pricing endpoints.
 *
 * Every function returns the unwrapped `data` payload on success or throws
 * a PricingError on failure. Network errors (fetch rejects) are caught and
 * wrapped as PricingError(0, "network_error", ...).
 */
import { PricingError } from "./types.js";
function authHeaders(token) {
    return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
    };
}
async function unwrap(response) {
    let body;
    try {
        body = (await response.json());
    }
    catch {
        throw new PricingError(response.status, "parse_error", `Unexpected response from Hub API (HTTP ${response.status})`);
    }
    if (!response.ok || !body.ok) {
        const err = body;
        throw new PricingError(response.status, err.error ?? "unknown", err.message ?? `Hub API error (HTTP ${response.status})`);
    }
    return body.data;
}
function wrapNetworkError(err) {
    if (err instanceof PricingError)
        throw err;
    const message = err instanceof Error ? err.message : "Network request failed";
    throw new PricingError(0, "network_error", message);
}
// ---------------------------------------------------------------------------
// Public API functions
// ---------------------------------------------------------------------------
/**
 * GET /v1/catalog — public endpoint (no auth required).
 */
export async function fetchCatalog(apiBase) {
    try {
        const res = await fetch(`${apiBase}/v1/catalog`);
        return unwrap(res);
    }
    catch (err) {
        return wrapNetworkError(err);
    }
}
/**
 * GET /v1/subscription — requires auth.
 * Pass `refresh: true` after checkout redirect to skip server cache.
 */
export async function fetchSubscription(apiBase, token, refresh) {
    try {
        const url = refresh
            ? `${apiBase}/v1/subscription?refresh=true`
            : `${apiBase}/v1/subscription`;
        const res = await fetch(url, { headers: authHeaders(token) });
        return unwrap(res);
    }
    catch (err) {
        return wrapNetworkError(err);
    }
}
/**
 * POST /v1/checkout — requires auth. Returns Stripe Checkout URL.
 */
export async function postCheckout(apiBase, token, opts) {
    try {
        const res = await fetch(`${apiBase}/v1/checkout`, {
            method: "POST",
            headers: authHeaders(token),
            body: JSON.stringify(opts),
        });
        return unwrap(res);
    }
    catch (err) {
        return wrapNetworkError(err);
    }
}
/**
 * POST /v1/subscription/upgrade — requires auth.
 *
 * HD-81: opts may include a `prorationDate` (unix seconds) returned by
 * `postUpgradePreview`. When present, the Hub passes it to Stripe as
 * `proration_date` to lock the quote. Stale dates (~5 min) trigger a
 * `proration_date_expired` error so the caller can re-preview.
 */
export async function postUpgrade(apiBase, token, opts) {
    try {
        const res = await fetch(`${apiBase}/v1/subscription/upgrade`, {
            method: "POST",
            headers: authHeaders(token),
            body: JSON.stringify(opts),
        });
        return unwrap(res);
    }
    catch (err) {
        return wrapNetworkError(err);
    }
}
/**
 * HD-81: POST /v1/subscription/upgrade/preview — requires auth.
 *
 * Read-only counterpart to /upgrade. Returns the prorated invoice Stripe
 * *would* generate without making any change, so the UI can confirm the
 * exact charge with the user before they commit.
 */
export async function postUpgradePreview(apiBase, token, opts) {
    try {
        const res = await fetch(`${apiBase}/v1/subscription/upgrade/preview`, {
            method: "POST",
            headers: authHeaders(token),
            body: JSON.stringify(opts),
        });
        return unwrap(res);
    }
    catch (err) {
        return wrapNetworkError(err);
    }
}
/**
 * DELETE /v1/subscription/scheduled-change/:product — requires auth.
 *
 * HD-119: releases a pending scheduled change (queued downgrade or
 * annual->monthly switch). The Hub guards that the change was user-initiated
 * (`not_cancellable` otherwise). Returns the released schedule id.
 */
export async function deleteScheduledChange(apiBase, token, product) {
    try {
        const res = await fetch(`${apiBase}/v1/subscription/scheduled-change/${encodeURIComponent(product)}`, { method: "DELETE", headers: authHeaders(token) });
        return unwrap(res);
    }
    catch (err) {
        return wrapNetworkError(err);
    }
}
/**
 * POST /v1/subscription/portal — requires auth.
 * Returns Stripe Customer Portal URL.
 */
export async function postPortal(apiBase, token, opts) {
    try {
        const res = await fetch(`${apiBase}/v1/subscription/portal`, {
            method: "POST",
            headers: authHeaders(token),
            body: JSON.stringify(opts ?? {}),
        });
        return unwrap(res);
    }
    catch (err) {
        return wrapNetworkError(err);
    }
}
